import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { COMPANY } from "../lib/constants";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Indtast dit navn.");
    if (!/\S+@\S+\.\S+/.test(form.email))
      return toast.error("Indtast en gyldig e-mail.");
    if (form.message.trim().length < 5)
      return toast.error("Skriv en kort besked (min. 5 tegn).");

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      });
      toast.success("Tak! Din besked er sendt — Johnny vender tilbage hurtigst muligt.");
      setDone(true);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(
        err?.response?.data?.detail ||
          "Beskeden kunne ikke sendes. Prøv igen eller ring direkte."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="kontakt"
      data-testid="contact-section"
      className="relative bg-[#0F213A] text-white py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: copy + details */}
          <div className="lg:col-span-5">
            <div className="eyebrow text-white/60 mb-4">
              <span className="inline-block w-8 h-[2px] bg-white/40 align-middle mr-3" />
              04 · Kontakt
            </div>
            <h2
              data-testid="contact-heading"
              className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]"
            >
              Klar til dit næste projekt?
            </h2>
            <p className="mt-6 text-white/75 text-base leading-relaxed max-w-md">
              Ring direkte eller send en besked — du hører tilbage inden for 24
              timer på hverdage. Altid uforpligtende.
            </p>

            <ul className="mt-10 space-y-5">
              <InfoRow
                icon={Phone}
                label="Ring direkte"
                value={COMPANY.phone}
                href={`tel:${COMPANY.phoneRaw}`}
                testid="contact-info-phone"
              />
              <InfoRow
                icon={Mail}
                label="E-mail"
                value={COMPANY.email}
                href={`mailto:${COMPANY.email}`}
                testid="contact-info-email"
              />
              <InfoRow
                icon={MapPin}
                label="Lokation"
                value={`${COMPANY.city} · Østjylland`}
                testid="contact-info-location"
              />
            </ul>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-white text-[#0F213A] p-6 md:p-10 shadow-2xl"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Field label="Navn*" htmlFor="name">
                  <input
                    id="name"
                    name="name"
                    type="text"
                    autoComplete="name"
                    data-testid="contact-field-name"
                    value={form.name}
                    onChange={onChange}
                    required
                    placeholder="Dit navn"
                    className="w-full bg-transparent border-b-2 border-[#E2E8F0] focus:border-[#0F213A] outline-none py-2 text-base"
                  />
                </Field>
                <Field label="Telefon" htmlFor="phone">
                  <input
                    id="phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    data-testid="contact-field-phone"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="+45"
                    className="w-full bg-transparent border-b-2 border-[#E2E8F0] focus:border-[#0F213A] outline-none py-2 text-base"
                  />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="E-mail*" htmlFor="email">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    data-testid="contact-field-email"
                    value={form.email}
                    onChange={onChange}
                    required
                    placeholder="dig@example.dk"
                    className="w-full bg-transparent border-b-2 border-[#E2E8F0] focus:border-[#0F213A] outline-none py-2 text-base"
                  />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Besked*" htmlFor="message">
                  <textarea
                    id="message"
                    name="message"
                    data-testid="contact-field-message"
                    value={form.message}
                    onChange={onChange}
                    required
                    rows={5}
                    placeholder="Fortæl kort om din opgave — sted, omfang og ønsket tidsramme."
                    className="w-full bg-transparent border-b-2 border-[#E2E8F0] focus:border-[#0F213A] outline-none py-2 text-base resize-none"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-btn"
                className="mt-8 w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#0F213A] text-white px-8 py-5 text-sm font-black uppercase tracking-[0.2em] btn-lift disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Sender…
                  </>
                ) : (
                  <>
                    <Send size={16} strokeWidth={2.5} />
                    Send besked
                  </>
                )}
              </button>

              {done && (
                <p
                  data-testid="contact-success"
                  className="mt-6 text-sm text-[#1E40AF] font-semibold"
                >
                  Tak! Din forespørgsel er modtaget.
                </p>
              )}

              <p className="mt-6 text-xs text-[#64748B]">
                Ved indsendelse accepterer du at vi gemmer din besked for at
                kunne svare dig. Dine oplysninger deles ikke med tredjepart.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <label htmlFor={htmlFor} className="block">
      <span className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#475569] block mb-1">
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoRow({ icon: Icon, label, value, href, testid }) {
  const inner = (
    <>
      <div className="w-11 h-11 shrink-0 border-2 border-white/25 flex items-center justify-center group-hover:border-white group-hover:bg-white transition-colors">
        <Icon
          size={18}
          strokeWidth={2}
          className="text-white group-hover:text-[#0F213A]"
        />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-white/60">
          {label}
        </div>
        <div className="text-base md:text-lg font-bold mt-0.5">{value}</div>
      </div>
    </>
  );
  if (href) {
    return (
      <li>
        <a
          href={href}
          data-testid={testid}
          className="group flex items-center gap-4 hover:text-white transition-colors"
        >
          {inner}
        </a>
      </li>
    );
  }
  return (
    <li data-testid={testid} className="group flex items-center gap-4">
      {inner}
    </li>
  );
}
